import type { ProductAvailability } from "~/components/atoms/TsRadioTrolleyCollection.vue";
import type { Product } from "~/types/ecom/product/product.type";
import type { TrolleyLineItem } from "~/types/ecom/trolley/trolley.type";

/* Utility method to check if an item is eligible for next business day collection */
export function checkItemForNextDayCollection(data: {
  productCode: string;
  collectionStock: number;
  quantityToAdd: number;
}) {
  const trolleyStore = useTrolleyStore();

  const { productCode, collectionStock, quantityToAdd } = data;

  const trolleyCollectionLineItem =
    trolleyStore.findTrolleyItemByProductCode(productCode);

  const totalQuantity =
    (trolleyCollectionLineItem?.quantity || 0) + quantityToAdd;

  const isCollectionStockInsufficient =
    !collectionStock || totalQuantity > collectionStock;

  return isCollectionStockInsufficient;
}

export function hideAllActionModals() {
  const compareStore = useCompareStore();
  const trolleyStore = useTrolleyStore();

  compareStore.enableExpand = false;
  compareStore.isCompareRowVisible = false;

  trolleyStore.preview_overlay_delivery_visible = false;
  trolleyStore.preview_overlay_collection_visible = false;
  trolleyStore.multiple_items_confirmation_modal_visible = false;
  trolleyStore.product_variants_modal_visible = false;
  trolleyStore.delivery_variants_modal_visible = false;
  trolleyStore.collection_variants_modal_visible = false;
  trolleyStore.preview_collection_delivery_modal_visible = false;
  trolleyStore.preview_overlay_visible = false;

  trolleyStore.remove_confirmation_modal_visible = false;
}

/**
 * Checks the collection availability status of a product based on the required quantity.
 *
 * @param options - The options object containing the product and required quantity.
 * @param options.productToBeProcessed - The product to check for collection availability.
 * @param options.requiredQuantity - The quantity of the product required by the user.
 * @returns Collection status of the product - "collection" | "next day collection" | "out of stock"
 */
export function checkCollectionAvailability(options: {
  productToBeProcessed: Product;
  requiredQuantity: number;
}): ProductAvailability {
  const { productToBeProcessed, requiredQuantity } = options;

  const enableNextDayCollection =
    requiredQuantity > productToBeProcessed.stockDetails?.collection! &&
    requiredQuantity <= productToBeProcessed.stockDetails?.delivery!;

  // Next day collection
  if (enableNextDayCollection)
    return {
      status: "NextDayCollection",
      stock: productToBeProcessed.stockDetails?.delivery!,
    };

  const enableOutOfStock =
    requiredQuantity > productToBeProcessed.stockDetails?.collection! &&
    requiredQuantity > productToBeProcessed.stockDetails?.delivery!;

  // Out of stock
  if (enableOutOfStock)
    return {
      status: "OutOfStock",
      stock: 0,
    };
  // Collection stock - success
  return {
    status: "Collection",
    stock: productToBeProcessed.stockDetails?.collection!,
  };
}

export function buildEncodedTrolleyUrl(trolleyItems: TrolleyLineItem[], trolleyCode: string) {
  const productString = trolleyItems
      .map(product => `${product.product_code}:${product.quantity}:${product.channel}`)
      .join(',');

  const queryString = `${trolleyCode}#${productString}`;
  const encodedString = window.btoa(queryString);

  return encodedString;
}